import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { RefreshCw, Database, Users, Activity, Table, Server, Clock, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OpenAIUsage } from "./OpenAIUsage";

interface QueueMetrics {
	queue_name: string;
	message_count: number;
}

interface TableSize {
	schemaname: string;
	tablename: string;
	total_size: string;
	size_bytes: number;
}

interface AuthStats {
	total_users: number;
	active_last_24h: number;
	active_last_7d: number;
	active_last_30d: number;
	unconfirmed_users: number;
}

interface OrgUserStats {
	organization_id: string;
	organization_name: string;
	total_users: number;
	active_last_24h: number;
	active_last_7d: number;
	active_last_30d: number;
}

interface ConnectionStats {
	total_connections: number;
	active_connections: number;
	idle_connections: number;
	idle_in_transaction: number;
}

interface DatabaseStats {
	database_size: string;
	database_size_bytes: number;
	real_size: string;
	real_size_bytes: number;
	bloat_size: string;
	bloat_size_bytes: number;
	bloat_ratio: number;
	public_tables_count: number;
	functions_count: number;
}

interface CacheStats {
	cache_hit_ratio: number;
}

interface SlowQueryStats {
	slow_queries_count: number;
	longest_running_seconds: number;
}

export const Statistics = () => {
	const [queueMetrics, setQueueMetrics] = useState<QueueMetrics[]>([]);
	const [tableSizes, setTableSizes] = useState<TableSize[]>([]);
	const [authStats, setAuthStats] = useState<AuthStats | null>(null);
	const [orgUserStats, setOrgUserStats] = useState<OrgUserStats[]>([]);
	const [connectionStats, setConnectionStats] = useState<ConnectionStats | null>(null);
	const [databaseStats, setDatabaseStats] = useState<DatabaseStats | null>(null);
	const [cacheStats, setCacheStats] = useState<CacheStats | null>(null);
	const [slowQueryStats, setSlowQueryStats] = useState<SlowQueryStats | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const { toast } = useToast();

	const fetchStatistics = async () => {
		setIsLoading(true);
		try {
			// Fetch queue metrics
			const { data: queues, error: queueError } = await supabase.rpc('get_queue_metrics' as any);
			if (queueError) throw queueError;
			setQueueMetrics(queues || []);

			// Fetch table sizes
			const { data: tables, error: tablesError } = await supabase.rpc('get_top_tables' as any);
			if (tablesError) throw tablesError;
			setTableSizes(tables || []);

			// Fetch auth stats
			const { data: auth, error: authError } = await supabase.rpc('get_auth_statistics' as any);
			if (authError) throw authError;
			setAuthStats(auth?.[0] || null);

			// Fetch organization user stats
			const { data: orgStats, error: orgStatsError } = await supabase.rpc('get_organization_user_stats' as any);
			if (orgStatsError) throw orgStatsError;
			setOrgUserStats(orgStats || []);

			// Fetch connection stats
			const { data: connections, error: connectionsError } = await supabase.rpc('get_connection_stats' as any);
			if (connectionsError) throw connectionsError;
			setConnectionStats(connections?.[0] || null);

			// Fetch database stats
			const { data: database, error: databaseError } = await supabase.rpc('get_database_stats' as any);
			if (databaseError) throw databaseError;
			setDatabaseStats(database?.[0] || null);

			// Fetch cache stats
			const { data: cache, error: cacheError } = await supabase.rpc('get_cache_stats' as any);
			if (cacheError) throw cacheError;
			setCacheStats(cache?.[0] || null);

			// Fetch slow query stats
			const { data: slowQueries, error: slowQueriesError } = await supabase.rpc('get_slow_query_stats' as any);
			if (slowQueriesError) throw slowQueriesError;
			setSlowQueryStats(slowQueries?.[0] || null);

		} catch (error: any) {
			toast({
				title: "Error fetching statistics",
				description: error.message,
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchStatistics();
	}, []);

	if (isLoading)
		return (
			<div className="flex justify-center py-8">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-digger-blue"></div>
			</div>
		);

	const totalQueueMessages = queueMetrics.reduce((sum, q) => sum + q.message_count, 0);

	return (
		<div className="space-y-6">
			<div className="flex justify-end">
				<Button onClick={fetchStatistics} variant="outline" size="sm">
					<RefreshCw className="mr-2 h-4 w-4" />
					Refresh
				</Button>
			</div>

			{/* Database Overview */}
			<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Database Size</CardTitle>
						<Database className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{databaseStats?.database_size || "N/A"}</div>
						<p className="text-xs text-muted-foreground">
							Real: {databaseStats?.real_size || "N/A"} | Bloat: {databaseStats?.bloat_size || "N/A"}
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Total Users</CardTitle>
						<Users className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{authStats?.total_users || 0}</div>
						<p className="text-xs text-muted-foreground">
							{authStats?.unconfirmed_users || 0} unconfirmed
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Active Connections</CardTitle>
						<Activity className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{connectionStats?.active_connections || 0}</div>
						<p className="text-xs text-muted-foreground">
							{connectionStats?.total_connections || 0} total, {connectionStats?.idle_connections || 0} idle
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Cache Hit Ratio</CardTitle>
						<Server className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{cacheStats?.cache_hit_ratio || 0}%</div>
						<p className="text-xs text-muted-foreground">
							{cacheStats && cacheStats.cache_hit_ratio >= 95 ? "Excellent" : "Needs optimization"}
						</p>
					</CardContent>
				</Card>
			</div>

			{/* Queue Metrics */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center">
						<Inbox className="mr-2 h-5 w-5" />
						PGMQ Queue Metrics
					</CardTitle>
					<CardDescription>
						Total messages: {totalQueueMessages}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-2">
						{queueMetrics.length === 0 ? (
							<p className="text-sm text-muted-foreground">No queue data available</p>
						) : (
							queueMetrics.map((queue) => (
								<div key={queue.queue_name} className="flex justify-between items-center py-2 border-b last:border-0">
									<span className="text-sm font-medium">{queue.queue_name}</span>
									<span className={`text-sm px-2 py-1 rounded ${queue.message_count > 0 ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800"}`}>
										{queue.message_count} messages
									</span>
								</div>
							))
						)}
					</div>
				</CardContent>
			</Card>

			{/* User Activity Stats by Organization */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center">
						<Users className="mr-2 h-5 w-5" />
						User Activity by Organization
					</CardTitle>
					<CardDescription>
						Active users breakdown per organization
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						{orgUserStats.length === 0 ? (
							<p className="text-sm text-muted-foreground">No organization data available</p>
						) : (
							<div className="overflow-x-auto">
								<table className="w-full text-sm">
									<thead className="border-b">
										<tr>
											<th className="text-left py-2 px-2 font-medium">Organization</th>
											<th className="text-right py-2 px-2 font-medium">Total</th>
											<th className="text-right py-2 px-2 font-medium">24h</th>
											<th className="text-right py-2 px-2 font-medium">7d</th>
											<th className="text-right py-2 px-2 font-medium">30d</th>
										</tr>
									</thead>
									<tbody>
										{orgUserStats.map((org) => (
											<tr key={org.organization_id} className="border-b last:border-0">
												<td className="py-2 px-2 font-medium">{org.organization_name}</td>
												<td className="text-right py-2 px-2">{org.total_users}</td>
												<td className="text-right py-2 px-2">{org.active_last_24h}</td>
												<td className="text-right py-2 px-2">{org.active_last_7d}</td>
												<td className="text-right py-2 px-2">{org.active_last_30d}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</div>
				</CardContent>
			</Card>

			{/* Database Bloat & WAL Stats */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center">
						<Database className="mr-2 h-5 w-5" />
						Database Storage Analysis
					</CardTitle>
					<CardDescription>
						Real data size vs bloat (vacuum/WAL logs)
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						<div className="grid gap-4 md:grid-cols-3">
							<div>
								<p className="text-sm text-muted-foreground">Total Database Size</p>
								<p className="text-2xl font-bold">{databaseStats?.database_size || "N/A"}</p>
							</div>
							<div>
								<p className="text-sm text-muted-foreground">Real Data Size</p>
								<p className="text-2xl font-bold">{databaseStats?.real_size || "N/A"}</p>
							</div>
							<div>
								<p className="text-sm text-muted-foreground">Bloat (WAL/Dead Tuples)</p>
								<p className="text-2xl font-bold">{databaseStats?.bloat_size || "N/A"}</p>
							</div>
						</div>
						<div>
							<div className="flex justify-between mb-1">
								<span className="text-sm font-medium">Bloat Ratio</span>
								<span className="text-sm">{databaseStats?.bloat_ratio || 0}%</span>
							</div>
							<div className="w-full bg-gray-200 rounded-sm h-2.5">
								<div
									className={`h-2.5 rounded-sm ${(databaseStats?.bloat_ratio || 0) < 10 ? "bg-green-600" : (databaseStats?.bloat_ratio || 0) < 20 ? "bg-yellow-600" : "bg-red-600"}`}
									style={{ width: `${databaseStats?.bloat_ratio || 0}%` }}
								></div>
							</div>
							<p className="text-xs text-muted-foreground mt-1">
								{(databaseStats?.bloat_ratio || 0) < 10 ? "Healthy" : (databaseStats?.bloat_ratio || 0) < 20 ? "Consider VACUUM" : "VACUUM recommended"}
							</p>
						</div>
						<div className="text-xs text-muted-foreground">
							<p className="font-medium mb-1">Storage Info:</p>
							<p>{databaseStats?.public_tables_count || 0} tables, {databaseStats?.functions_count || 0} functions</p>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Performance Metrics */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center">
						<Clock className="mr-2 h-5 w-5" />
						Performance Metrics
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						<div>
							<div className="flex justify-between mb-1">
								<span className="text-sm font-medium">Cache Hit Ratio</span>
								<span className="text-sm">{cacheStats?.cache_hit_ratio || 0}%</span>
							</div>
							<div className="w-full bg-gray-200 rounded-sm h-2.5">
								<div
									className={`h-2.5 rounded-sm ${(cacheStats?.cache_hit_ratio || 0) >= 95 ? "bg-green-600" : "bg-yellow-600"}`}
									style={{ width: `${cacheStats?.cache_hit_ratio || 0}%` }}
								></div>
							</div>
						</div>
						<div className="grid gap-4 md:grid-cols-2">
							<div>
								<p className="text-sm text-muted-foreground">Slow Queries (&gt;1s)</p>
								<p className="text-2xl font-bold">{slowQueryStats?.slow_queries_count || 0}</p>
							</div>
							<div>
								<p className="text-sm text-muted-foreground">Longest Running Query</p>
								<p className="text-2xl font-bold">
									{slowQueryStats?.longest_running_seconds
										? `${Math.round(slowQueryStats.longest_running_seconds)}s`
										: "N/A"}
								</p>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Top 10 Largest Tables */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center">
						<Table className="mr-2 h-5 w-5" />
						Top 10 Largest Tables
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="space-y-2">
						{tableSizes.length === 0 ? (
							<p className="text-sm text-muted-foreground">No table data available</p>
						) : (
							tableSizes.map((table, index) => (
								<div key={`${table.schemaname}.${table.tablename}`} className="flex justify-between items-center py-2 border-b last:border-0">
									<div className="flex items-center gap-2">
										<span className="text-xs text-muted-foreground w-6">#{index + 1}</span>
										<div>
											<span className="text-sm font-medium">{table.tablename}</span>
											<span className="text-xs text-muted-foreground ml-2">({table.schemaname})</span>
										</div>
									</div>
									<span className="text-sm font-semibold">{table.total_size}</span>
								</div>
							))
						)}
					</div>
				</CardContent>
			</Card>

            {/* OpenAI API Usage */}
            <Card>
				<CardHeader>
					<CardTitle>OpenAI API Usage</CardTitle>
					<CardDescription>
						Token usage and costs from OpenAI API
					</CardDescription>
				</CardHeader>
				<CardContent>
					<OpenAIUsage />
				</CardContent>
			</Card>

		</div>
	);
};
